import React, { PropsWithChildren, useEffect, useState, useRef } from 'react';
import { Flex } from '@ui-library/flex';
import loader from '@svg/animated-logo-mark.svg?url';
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Spin } from 'antd';
import { useTheme } from 'styled-components';
import { BaseText } from '@ui-library/typography';
import { FitParent, LoaderContainer, LoaderImage, SpinnerContainer } from './styles';

type Props =
  | {
      type: 'spin';
      size?: 16 | 24 | 36 | 64;
      loading?: boolean;
    }
  | {
      type: 'screen' | 'fitParent';
      loading: boolean;
    };

const LOADING_PHRASES = [
  'Loading workspace',
  'Analyzing bits',
  'Mining nuggets',
  'Reticulating splines',
  'Crunching numbers',
  'Synchromeshing gears',
];

export const Loader: React.FC<PropsWithChildren<Props>> = (props: PropsWithChildren<Props>) => {
  const { token } = useTheme();
  const [loadingPhraseIndex, setLoadingPhraseIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const initialDelay = 1000;
    const intervalDelay = 2000;

    const timeout = setTimeout(() => {
      const interval = setInterval(() => {
        setLoadingPhraseIndex((prevIndex) => (prevIndex + 1) % LOADING_PHRASES.length);
      }, intervalDelay);

      return () => clearInterval(interval);
    }, initialDelay);

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (textRef.current) {
      // 50px is spinner + margin + ellipsis
      setContainerWidth(textRef.current.offsetWidth + 50);
    }
  }, [loadingPhraseIndex]);

  if (props.type === 'spin') {
    const { size = 24, children, loading = true } = props;
    return (
      <Spin
        spinning={loading}
        indicator={
          <FontAwesomeIcon
            style={{ fontSize: size }}
            icon={regular('circle-notch')}
            className="fa-spin"
            color={token.colorPrimary}
          />
        }
      >
        {children}
      </Spin>
    );
  }

  if (!props.loading) {
    return null;
  }

  const Container = props.type === 'fitParent' ? FitParent : LoaderContainer;

  return (
    <Container data-testid="loader">
      <Flex flexDirection="column" justifyContent="center" alignItems="center">
        <LoaderImage alt="loader" src={loader} />
        <SpinnerContainer alignItems="center" style={{ width: containerWidth }}>
          <FontAwesomeIcon
            style={{ fontSize: 20, marginRight: 16 }}
            icon={regular('circle-notch')}
            color={token.colorPrimary}
            spin
          />
          <BaseText color={token.colorTextDescription} className="space-nowrap" ref={textRef}>
            {LOADING_PHRASES[loadingPhraseIndex]}...
          </BaseText>
        </SpinnerContainer>
      </Flex>
    </Container>
  );
};
